import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ROLE_LABELS, normRole, STAFF_ROLES } from "@/lib/permissions";
import { UserCog, LayoutDashboard, Loader2, Mail, Phone, MapPin } from "lucide-react";

export default function StaffManagement() {
  const navigate = useNavigate();
  const [data, setData] = useState({ staff: [], zones: [], can_manage: false });
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/staff", { params: { role: roleFilter !== "all" ? roleFilter : undefined } });
      setData(res.data);
    } catch { toast.error("Failed to load staff"); }
    finally { setLoading(false); }
  }, [roleFilter]);

  useEffect(() => { load(); }, [load]);

  const setZone = async (u, zoneId) => {
    try {
      await api.post(`/admin/staff/${u.id}/zone`, { zone_id: zoneId === "none" ? null : zoneId });
      toast.success(`Zone updated for ${u.name || u.email}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update zone"); }
  };

  return (
    <div className="min-h-screen bg-[#f8faf8]">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-head font-extrabold text-2xl sm:text-3xl text-emerald-950 flex items-center gap-2"><UserCog className="h-7 w-7 text-emerald-700" /> Staff Management</h1>
            <p className="mt-1.5 text-slate-500">Your staff and channel partners, their roles and assigned zones.</p>
          </div>
          <Button variant="ghost" className="rounded-full text-emerald-700 h-10" onClick={() => navigate("/admin")} data-testid="staff-back-console"><LayoutDashboard className="h-4 w-4 mr-1.5" /> Console</Button>
        </div>

        <div className="mt-6 flex flex-wrap gap-2" data-testid="staff-role-filter">
          {["all", ...STAFF_ROLES].map((r) => (
            <button key={r} onClick={() => setRoleFilter(r)} data-testid={`staff-filter-${r}`}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${roleFilter === r ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"}`}>
              {r === "all" ? "All" : ROLE_LABELS[r]}
            </button>
          ))}
        </div>

        <Card className="mt-5 rounded-3xl border-emerald-100 overflow-hidden">
          {loading ? (
            <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>
          ) : data.staff.length === 0 ? (
            <div className="py-16 text-center text-slate-400" data-testid="staff-empty">No staff members found.</div>
          ) : (
            <div className="divide-y divide-emerald-50" data-testid="staff-table">
              {data.staff.map((u) => (
                <div key={u.id} className="flex flex-wrap items-center gap-4 px-5 py-4">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-800 truncate">{u.name || "—"}</div>
                    <div className="text-xs text-slate-400 flex flex-wrap gap-3 mt-0.5">
                      {u.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {u.email}</span>}
                      {u.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {u.phone}</span>}
                    </div>
                  </div>
                  <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">{ROLE_LABELS[normRole(u.role)]}</span>
                  <div className="w-48">
                    {data.can_manage ? (
                      <Select value={u.zone_id || "none"} onValueChange={(v) => setZone(u, v)}>
                        <SelectTrigger className="h-9 rounded-full bg-white text-xs" data-testid={`staff-zone-${u.id}`}><SelectValue placeholder="Assign zone" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No zone</SelectItem>
                          {data.zones.map((z) => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-xs text-slate-500 inline-flex items-center gap-1"><MapPin className="h-3 w-3 text-slate-400" /> {u.zone_name || "No zone"}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
